import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format, parseISO, isToday, isFuture, startOfDay } from "date-fns";
import { fetchUpcomingPickups, fetchAllPartnerPickups, createChangeRequest } from "api";
import { Spinner, StatusBadge, Modal, FormField } from "components/common";
import type { ScheduleItem, ScheduleItemChangeRequest, ChangeRequestDetails } from "types";

// ─── Partner Home Page ────────────────────────────────────────────────────────

export default function PartnerHomePage() {
  const { data: items = [], isLoading } = useQuery({
    queryKey: ["upcomingPickups"],
    queryFn:  fetchUpcomingPickups,
  });

  const [timeChangeTarget,   setTimeChangeTarget]   = useState<ScheduleItem | null>(null);
  const [cancellationTarget, setCancellationTarget] = useState<ScheduleItem | null>(null);
  const [activeMenu,         setActiveMenu]         = useState<number | null>(null);

  const queryClient = useQueryClient();
  const invalidate  = () => queryClient.invalidateQueries({ queryKey: ["upcomingPickups"] });

  // Split into today vs future-not-today
  const todayItems = items.filter((i) => isToday(parseISO(i.scheduled_at)));
  const soonItems  = items.filter((i) => {
    const d = parseISO(i.scheduled_at);
    return isFuture(startOfDay(d)) && !isToday(d);
  });

  const totalCount = items.length;

  const cardProps = (item: ScheduleItem) => ({
    key:                  item.id,
    item,
    isMenuOpen:           activeMenu === item.id,
    onToggleMenu:         (e: React.MouseEvent) => { e.stopPropagation(); setActiveMenu(activeMenu === item.id ? null : item.id); },
    onCloseMenu:          () => setActiveMenu(null),
    onRequestTimeChange:  () => { setActiveMenu(null); setTimeChangeTarget(item); },
    onRequestCancellation:() => { setActiveMenu(null); setCancellationTarget(item); },
  });

  if (isLoading) return <Spinner className="mt-20" />;

  return (
    <div className="px-4 py-6 sm:px-6 sm:py-8 max-w-2xl mx-auto">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Upcoming pickups</h1>
          <p className="text-sm text-gray-500 mt-1">
            {totalCount === 0
              ? "No upcoming pickups scheduled."
              : `${totalCount} pickup${totalCount === 1 ? "" : "s"} coming up`}
          </p>
        </div>
        <Link
          to="/pickups"
          className="text-sm text-brand-600 hover:text-brand-700 transition-colors flex items-center gap-1 mt-1 shrink-0"
        >
          View all
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </Link>
      </div>

      {/* ── Today ──────────────────────────────────────────────────────────── */}
      <div className="border-2 border-dashed border-gray-200 rounded-2xl p-4 mb-4">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">Today</p>
        {todayItems.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-3">Nothing scheduled today</p>
        ) : (
          <div className="space-y-3">
            {todayItems.map((item) => <PickupCard {...cardProps(item)} />)}
          </div>
        )}
      </div>

      {/* ── Happening soon ─────────────────────────────────────────────────── */}
      <div className="border border-gray-200 rounded-2xl p-4">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">Happening soon</p>
        {soonItems.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-3">No upcoming pickups</p>
        ) : (
          <div className="space-y-3">
            {soonItems.map((item) => <PickupCard {...cardProps(item)} />)}
          </div>
        )}
      </div>

      {/* ── Modals ─────────────────────────────────────────────────────────── */}
      {timeChangeTarget && (
        <RequestTimeChangeModal
          item={timeChangeTarget}
          onClose={() => setTimeChangeTarget(null)}
          onSuccess={() => { invalidate(); setTimeChangeTarget(null); }}
        />
      )}
      {cancellationTarget && (
        <RequestCancellationModal
          item={cancellationTarget}
          onClose={() => setCancellationTarget(null)}
          onSuccess={() => { invalidate(); setCancellationTarget(null); }}
        />
      )}
    </div>
  );
}

// ─── Partner All Pickups Page ─────────────────────────────────────────────────

export function PartnerAllPickupsPage() {
  const { data: items = [], isLoading } = useQuery({
    queryKey: ["allPartnerPickups"],
    queryFn:  fetchAllPartnerPickups,
  });

  const [timeChangeTarget,   setTimeChangeTarget]   = useState<ScheduleItem | null>(null);
  const [cancellationTarget, setCancellationTarget] = useState<ScheduleItem | null>(null);
  const [activeMenu,         setActiveMenu]         = useState<number | null>(null);

  const queryClient = useQueryClient();
  const invalidate  = () => queryClient.invalidateQueries({ queryKey: ["allPartnerPickups"] });

  const cardProps = (item: ScheduleItem) => ({
    key:                  item.id,
    item,
    isMenuOpen:           activeMenu === item.id,
    onToggleMenu:         (e: React.MouseEvent) => { e.stopPropagation(); setActiveMenu(activeMenu === item.id ? null : item.id); },
    onCloseMenu:          () => setActiveMenu(null),
    onRequestTimeChange:  () => { setActiveMenu(null); setTimeChangeTarget(item); },
    onRequestCancellation:() => { setActiveMenu(null); setCancellationTarget(item); },
  });

  if (isLoading) return <Spinner className="mt-20" />;

  // Group by month label, sorted chronologically
  const grouped = items.reduce<Record<string, ScheduleItem[]>>((acc, item) => {
    const key = format(parseISO(item.scheduled_at), "MMMM yyyy");
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});

  return (
    <div className="px-4 py-6 sm:px-6 sm:py-8 max-w-2xl mx-auto">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-3 mb-8">
        <Link
          to="/"
          className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          title="Back"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </Link>
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">All pickups</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {items.length === 0 ? "No pickups on record." : `${items.length} total`}
          </p>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="card p-10 text-center">
          <div className="text-4xl mb-3">📦</div>
          <p className="text-sm text-gray-500">No pickups on record yet.</p>
        </div>
      ) : (
        <div className="space-y-8">
          {Object.entries(grouped).map(([month, monthItems]) => (
            <div key={month}>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">
                {month}
              </p>
              <div className="space-y-3">
                {monthItems.map((item) => <PickupCard {...cardProps(item)} />)}
              </div>
            </div>
          ))}
        </div>
      )}

      {timeChangeTarget && (
        <RequestTimeChangeModal
          item={timeChangeTarget}
          onClose={() => setTimeChangeTarget(null)}
          onSuccess={() => { invalidate(); setTimeChangeTarget(null); }}
        />
      )}
      {cancellationTarget && (
        <RequestCancellationModal
          item={cancellationTarget}
          onClose={() => setCancellationTarget(null)}
          onSuccess={() => { invalidate(); setCancellationTarget(null); }}
        />
      )}
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Convert an HH:MM string (from <input type="time">) to 12-hour format, e.g. "2:30pm" */
function fmt12h(hhmm: string): string {
  const [h, m] = hhmm.split(":").map(Number);
  const period = h >= 12 ? "pm" : "am";
  const hour12 = h % 12 || 12;
  return `${hour12}:${String(m).padStart(2, "0")}${period}`;
}

// ─── Pickup Card ──────────────────────────────────────────────────────────────

function PickupCard({
  item,
  isMenuOpen,
  onToggleMenu,
  onCloseMenu,
  onRequestTimeChange,
  onRequestCancellation,
}: {
  item:                 ScheduleItem;
  isMenuOpen:           boolean;
  onToggleMenu:         (e: React.MouseEvent) => void;
  onCloseMenu:          () => void;
  onRequestTimeChange:  () => void;
  onRequestCancellation: () => void;
}) {
  const scheduledAt = parseISO(item.scheduled_at);
  const dayOfWeek   = format(scheduledAt, "EEEE").toUpperCase();
  const monthLabel  = format(scheduledAt, "MMM").toUpperCase();
  const dayNumber   = format(scheduledAt, "d");
  const timeLabel   = scheduledAt.toLocaleTimeString([], {
    hour: "numeric", minute: "2-digit", hour12: true, timeZoneName: "short",
  });

  const courier     = item.contact_user ?? null;
  const courierName = courier
    ? `${courier.name_1}${courier.name_2 ? ` ${courier.name_2}` : ""}`
    : null;
  const initials    = courier
    ? `${courier.name_1[0]}${courier.name_2 ? courier.name_2[0] : courier.name_1[1] ?? ""}`.toUpperCase()
    : null;

  const pendingRequests = (item.change_requests ?? []).filter((r) => r.status === "requested");
  const foodLogs        = item.food_logs ?? [];
  const canRequest      = ["created", "pending_assignment", "scheduled"].includes(item.status);

  return (
    // No overflow-hidden on the outer wrapper so the avatar can straddle the boundary
    <div className="rounded-2xl border border-gray-300 shadow-sm bg-white relative">

      {/* ── Header band ──────────────────────────────────────────────────────── */}
      {/* overflow-visible lets the avatar spill below; border-radius clips the bg */}
      <div className="bg-gray-100 rounded-t-2xl overflow-visible relative px-5 pt-1 pb-1 pl-[88px]">

        {/* Date label row */}


        {/* Calendar badge + time on the same row */}
        <div className="flex items-center justify-between gap-3">


          {/* Time — bold and prominent */}
          <div className="flex ml-4 px-6">
            <span className="text-xs bg-blue-800 font-bold text-white py-2 px-3 uppercase tracking-wide leading-tight mr-2 rounded-xl">
              {dayOfWeek}
            </span>
            <span className="text-l font-bold text-gray-800 pt-[3px] tracking-tight">
              {timeLabel}
            </span>
          </div>

          {/* Calendar badge — right-aligned */}
          <div className="ml-auto">
            <div className="w-12 rounded-xl overflow-hidden shadow-sm border border-white/70 select-none shrink-0">
              <div className="bg-red-500 py-0.5 text-center">
                <span className="text-white text-[10px] font-bold tracking-widest leading-none">{monthLabel}</span>
              </div>
              <div className="bg-white py-1 text-center">
                <span className="text-gray-900 text-xl font-bold leading-none">{dayNumber}</span>
              </div>
            </div>
          </div>
        </div>

        {/* ── Courier avatar — centered on the header/body boundary ─────────── */}
        <div className="absolute bottom-0 translate-y-1/2 left-5 z-10">
          {courier?.profile_photo_url ? (
            <img
              src={courier.profile_photo_url}
              alt={courierName ?? "Courier"}
              className="w-16 h-16 rounded-full object-cover ring-[3px] ring-white shadow-md"
            />
          ) : (
            <div className={`w-16 h-16 rounded-full flex items-center justify-center text-lg font-semibold ring-[3px] ring-white shadow-md ${
              courier ? "bg-indigo-100 text-indigo-700" : "bg-gray-100 text-gray-400"
            }`}>
              {initials ?? (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Body ─────────────────────────────────────────────────────────────── */}
      <div className="rounded-b-2xl px-5 pt-10 pb-4 pl-[88px]">

        {/* Courier name */}
        <p className="text-sm font-semibold text-gray-800 leading-snug">
          Courier: {courierName ?? <span className="text-gray-400 font-normal italic">Courier not yet assigned</span>}
        </p>

        {/* Status + gear actions menu */}
        <div className="flex items-center justify-between mt-2">
          <StatusBadge status={item.status} />

          {canRequest && (
            <div className="relative">
              <button
                onClick={onToggleMenu}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                title="Actions"
              >
                {/* Gear icon */}
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="3" />
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                </svg>
              </button>

              {isMenuOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={onCloseMenu} />
                  <div className="absolute right-0 top-full mt-1 z-20 w-52 bg-white rounded-xl shadow-lg border border-gray-100 py-1 overflow-hidden">
                    <button
                      onClick={onRequestTimeChange}
                      className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2.5"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" />
                        <polyline points="12 6 12 12 16 14" />
                      </svg>
                      Request time change
                    </button>
                    <button
                      onClick={onRequestCancellation}
                      className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors flex items-center gap-2.5"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="15" y1="9" x2="9" y2="15" />
                        <line x1="9"  y1="9" x2="15" y2="15" />
                      </svg>
                      Request cancellation
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Food logs */}
        {foodLogs.length > 0 && (
          <div className="flex items-center gap-1.5 mt-3 text-xs text-gray-500">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 text-gray-400">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
            </svg>
            {foodLogs.length} food log{foodLogs.length === 1 ? "" : "s"}
            {foodLogs.reduce((sum, l) => sum + l.poundage, 0) > 0 && (
              <> &mdash; {foodLogs.reduce((sum, l) => sum + l.poundage, 0)} lbs</>
            )}
          </div>
        )}

        {/* Pending change requests */}
        {pendingRequests.length > 0 && (
          <div className="mt-3 space-y-1.5">
            {pendingRequests.map((req) => (
              <PendingRequestBadge key={req.id} request={req} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Pending Request Badge ────────────────────────────────────────────────────

function PendingRequestBadge({ request }: { request: ScheduleItemChangeRequest }) {
  if (request.change_type === "cancellation") {
    return (
      <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-1.5">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
        Cancellation requested &mdash; pending review
      </div>
    );
  }

  const d = request.details;
  return (
    <div className="flex items-center gap-2 text-xs text-blue-700 bg-blue-50 rounded-lg px-3 py-1.5">
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16 14" />
      </svg>
      Time change requested
      {d && (
        <> &mdash; {format(parseISO(d.date), "MMM d")}, {fmt12h(d.start_time)}–{fmt12h(d.end_time)}</>
      )}
      &nbsp;&mdash; pending review
    </div>
  );
}

// ─── Request Time Change Modal ────────────────────────────────────────────────

function RequestTimeChangeModal({
  item,
  onClose,
  onSuccess,
}: {
  item:      ScheduleItem;
  onClose:   () => void;
  onSuccess: () => void;
}) {
  const scheduledAt = parseISO(item.scheduled_at);
  const [date,      setDate]      = useState(format(scheduledAt, "yyyy-MM-dd"));
  const [startTime, setStartTime] = useState("");
  const [endTime,   setEndTime]   = useState("");

  const isValid = date && startTime && endTime && startTime < endTime;

  const { mutate, isPending, error } = useMutation({
    mutationFn: () => {
      // Construct a UTC ISO timestamp from the local date + start_time so the
      // backend can apply the correct time regardless of its own timezone config.
      const scheduledAt = new Date(`${date}T${startTime}`).toISOString();
      return createChangeRequest(item.id, {
        change_type: "time_change",
        details: { date, start_time: startTime, end_time: endTime, scheduled_at: scheduledAt },
      });
    },
    onSuccess,
  });

  return (
    <Modal title="Request time change" onClose={onClose}>
      <div className="space-y-4">
        <p className="text-sm text-gray-500">
          Select a new preferred time window for this pickup. Your request will be reviewed by the team.
        </p>

        <FormField label="Date">
          <input
            type="date"
            className="input"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </FormField>

        <div className="grid grid-cols-2 gap-3">
          <FormField label="Start time">
            <input
              type="time"
              className="input"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
            />
          </FormField>
          <FormField label="End time">
            <input
              type="time"
              className="input"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
            />
          </FormField>
        </div>

        {startTime && endTime && startTime >= endTime && (
          <p className="text-xs text-red-500">End time must be after start time.</p>
        )}

        {error && (
          <p className="text-xs text-red-500">Failed to submit request. Please try again.</p>
        )}

        <div className="flex gap-3 pt-1">
          <button className="btn-secondary flex-1" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-primary flex-1"
            disabled={!isValid || isPending}
            onClick={() => mutate()}
          >
            {isPending ? "Submitting…" : "Submit request"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Request Cancellation Modal ───────────────────────────────────────────────

function RequestCancellationModal({
  item,
  onClose,
  onSuccess,
}: {
  item:      ScheduleItem;
  onClose:   () => void;
  onSuccess: () => void;
}) {
  const scheduledAt = parseISO(item.scheduled_at);

  const { mutate, isPending, error } = useMutation({
    mutationFn: () =>
      createChangeRequest(item.id, { change_type: "cancellation" }),
    onSuccess,
  });

  return (
    <Modal title="Request cancellation" onClose={onClose}>
      <div className="space-y-4">
        <p className="text-sm text-gray-700">
          Are you sure you want to request cancellation of the pickup scheduled for{" "}
          <strong>{format(scheduledAt, "EEEE, MMMM d")}</strong> at{" "}
          <strong>{scheduledAt.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</strong>?
        </p>
        <p className="text-xs text-gray-500">
          This is a request, not an immediate cancellation. The team will review and confirm.
        </p>

        {error && (
          <p className="text-xs text-red-500">Failed to submit request. Please try again.</p>
        )}

        <div className="flex gap-3 pt-1">
          <button className="btn-secondary flex-1" onClick={onClose}>
            Keep pickup
          </button>
          <button
            className="flex-1 px-4 py-2 rounded-lg text-sm font-medium bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 transition-colors"
            disabled={isPending}
            onClick={() => mutate()}
          >
            {isPending ? "Submitting…" : "Request cancellation"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
